import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { noop } from 'lodash-es';
import { RevenueOpportunitiesSectionState } from 'anchor-scout-common/types';
import { useOverlayContext } from 'seagull';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { sectionView, unrecognisedStateReport } from '../RevenueOpportunitiesSection.utils';
import { describeCard, matchCard, opportunityBiData, partitionByRecognisedKind, resolveFrontIndex, unrecognisedKindReport } from '../utils/opportunityCard';
import { opportunityRoute } from '../utils/opportunityDestination';
import { dismissOptionLabel, dismissOptions } from '../utils/opportunityDismissals';
import { useRevenueOpportunitiesSummary } from './useRevenueOpportunitiesSummary';
import { useRevenueOpportunityDismissals } from './useRevenueOpportunityDismissals';
import { useSectionPreferences } from './useSectionPreferences';
const NO_CARDS = [];
export const useRevenueOpportunitiesSection = (toastTestIds)=>{
    var _ref;
    const navigate = useNavigate();
    const { browser } = useIoContext();
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { showInfoToast } = useOverlayContext();
    const { summary, isLoading, error, refresh } = useRevenueOpportunitiesSummary({
        refreshOnHookMount: true
    });
    const { isDismissing, dismissCard } = useRevenueOpportunityDismissals(toastTestIds);
    const { isCollapsed, toggleCollapsed, isHiddenWhileDone, hideWhileDone, releaseHide } = useSectionPreferences();
    const [front, setFront] = useState({
        identity: null,
        index: 0
    });
    const { recognised: cards, unrecognised } = useMemo(()=>{
        var _ref;
        return partitionByRecognisedKind((_ref = summary === null || summary === void 0 ? void 0 : summary.cards) !== null && _ref !== void 0 ? _ref : NO_CARDS);
    }, [
        summary
    ]);
    const cardViews = useMemo(()=>cards.map((card)=>describeCard(card, texts)), [
        cards
    ]);
    const frontIndex = resolveFrontIndex(cardViews.map((view)=>view.identity), front);
    const frontCard = cards[frontIndex];
    const frontView = cardViews[frontIndex];
    const lastSyncedAt = (_ref = summary === null || summary === void 0 ? void 0 : summary.lastSyncedAt) !== null && _ref !== void 0 ? _ref : null;
    const view = sectionView({
        isLoading,
        hasFailed: error != null,
        summary,
        recognisedCardCount: cards.length,
        isHiddenWhileDone
    });
    const scoutState = summary === null || summary === void 0 ? void 0 : summary.state;
    const skippedReport = unrecognisedKindReport(unrecognised);
    const unknownStateReport = unrecognisedStateReport(scoutState);
    useEffect(()=>{
        if (scoutState != null && scoutState !== RevenueOpportunitiesSectionState.Done) {
            releaseHide();
        }
    }, [
        scoutState,
        releaseHide
    ]);
    useEffect(()=>{
        if (skippedReport != null) {
            browser.reportError(new Error(skippedReport));
        }
    }, [
        skippedReport
    ]);
    useEffect(()=>{
        if (unknownStateReport != null) {
            browser.reportError(new Error(unknownStateReport));
        }
    }, [
        unknownStateReport
    ]);
    function showCard(index) {
        setFront({
            identity: cardViews[index].identity,
            index
        });
    }
    function showNextCard() {
        showCard((frontIndex + 1) % cardViews.length);
    }
    function actOnFrontCard() {
        matchCard(frontCard, {
            single: (param)=>{
                let { opportunity } = param;
                return navigate(opportunityRoute(opportunity));
            },
            group: noop
        });
    }
    function swipeDeck(direction) {
        if (direction === 'left') {
            showNextCard();
        }
    }
    function hideForNow() {
        hideWhileDone();
        showInfoToast(texts.section.toasts.hiddenForNow(), toastTestIds.HiddenForNow());
    }
    function retry() {
        void refresh({
            throwOnError: false
        });
    }
    function dismissFrontCard(option) {
        void dismissCard(frontCard, frontView.identity, option, opportunityBiData(frontView, frontIndex));
    }
    return {
        view,
        retry,
        hideForNow,
        lastSyncedDate: lastSyncedAt == null ? null : timeModel.formatDate(lastSyncedAt, {
            long: true
        }),
        isCollapsed,
        toggleCollapsed,
        cardViews,
        frontIndex,
        showCard,
        showNextCard: cardViews.length > 1 ? showNextCard : undefined,
        actOnFrontCard,
        frontDismissOptions: frontCard ? dismissOptions(frontCard).map((option)=>({
                option,
                label: dismissOptionLabel(option, frontView.kind, texts)
            })) : [],
        isFrontDismissing: frontView != null && isDismissing(frontView.identity),
        dismissFrontCard,
        swipeDeck
    };
};
